import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Building2, Plus, ExternalLink, ShieldCheck, Users, Copy, Upload, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AppLayout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  all: { eyebrow: 'organizations', title: 'Organizations', cta: 'New organization',
         line: 'Every organization on the platform.' },
  demo: { eyebrow: 'demos', title: 'Demos', cta: 'New demo',
          line: "Throwaway organizations for sales calls and investor walkthroughs, plus the lab sessions below. Nothing here is a paying client." },
  pilot: { eyebrow: 'pilots', title: 'Pilots', cta: 'New pilot',
           line: 'Organizations trying FuturePath with real young people before they pay. Same instance as a paid client, just marked as a pilot.' },
  paid: { eyebrow: 'paid clients', title: 'Paid Clients', cta: 'New client',
          line: 'Organizations paying for seats. Create one, brand it, set their places and invite their admin, no developer needed.' },
};

const LICENCES = [
  { key: 'demo', label: 'Demo', line: 'A walkthrough for sales or investors' },
  { key: 'pilot', label: 'Pilot', line: 'Real young people, not paying yet' },
  { key: 'paid', label: 'Paid client', line: 'Paying for seats' },
];

const BLANK = {
  name: '', org_type: 'youth_development', slug: '', description: '', location: '', website: '',
  license_type: 'paid',
  primary_contact_name: '', primary_contact_email: '', participant_age_min: '',
  participant_age_max: '', seat_count: '', credits_per_seat: '', license_fee: '',
  admin_name: '', admin_email: '', logo_url: '',
  primary_color: '#22d3ee', secondary_color: '#a78bfa',
};

/** FuturePath staff view: every tenant, and the door into each one. */
export default function AdminOrganizationsPage({ embedded = false, licence = null }) {
  const { getAuthHeader } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(BLANK);
  const [slugState, setSlugState] = useState(null);
  const [created, setCreated] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/orgs`, getAuthHeader()).then(r => setOrgs(
      (r.data.organizations || []).filter(o => !licence
        || (o.license_type || 'paid') === licence
        || (licence === 'paid' && !['demo', 'pilot'].includes(o.license_type)))
    )).catch(() => {});
    axios.get(`${API}/orgs/types`).then(r => setTypes(r.data.types)).catch(() => {});
  }, [getAuthHeader]);
  useEffect(load, [load]);
  useEffect(() => { if (licence) setF(v => ({ ...v, license_type: licence })); }, [licence]);

  const checkSlug = async (value) => {
    setF(v => ({ ...v, slug: value }));
    if (value.length < 3) return setSlugState(null);
    try {
      const r = await axios.get(`${API}/orgs/slug-available/${value}`, getAuthHeader());
      setSlugState(r.data);
    } catch { setSlugState(null); }
  };

  const pickLogo = (file) => {
    if (!file) return;
    if (file.size > 600000) return toast.error('Keep the logo under 600KB');
    const reader = new FileReader();
    reader.onload = () => setF(v => ({ ...v, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      ['participant_age_min', 'participant_age_max', 'seat_count', 'credits_per_seat', 'license_fee']
        .forEach(k => { payload[k] = payload[k] === '' ? null : Number(payload[k]); });
      if (payload.seat_count === null) payload.seat_count = 0;
      if (!payload.slug) delete payload.slug;
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const r = await axios.post(`${API}/orgs`, payload, getAuthHeader());
      setCreated({ ...r.data, name: r.data.organization.name });
      setOpen(false);
      setF(BLANK);
      setSlugState(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail
        || 'Could not create that organization');
    } finally { setSaving(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied');
  };

  const Shell = embedded ? Fragment : AppLayout;

  return (
    <Shell>
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-organizations-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">
            FuturePath · {LABELS[licence || 'all'].eyebrow}
          </p>
          <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-white mt-2">
            {LABELS[licence || 'all'].title}
          </h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl">{LABELS[licence || 'all'].line}</p>
        </div>
        <Button onClick={() => { setF({ ...BLANK, license_type: licence || 'paid' }); setOpen(true); }}
          data-testid="create-org-btn"
          className="ml-auto rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> {LABELS[licence || 'all'].cta}
        </Button>
      </motion.div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-8" data-testid="org-list">
        {orgs.map((o, i) => {
          const s = o.seats || { total: 0, used: o.participants || 0, label: '' };
          const pct = s.total ? Math.min(100, Math.round((s.used / s.total) * 100)) : 0;
          const colour = o.branding?.primary_color || '#22d3ee';
          return (
            <motion.div key={o.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }} data-testid={`org-card-${o.slug}`}
              className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 hover:border-cyan-400/40 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
                  style={{ background: `${colour}22`, border: `1px solid ${colour}55` }}>
                  {o.branding?.logo_url
                    ? <img src={o.branding.logo_url} alt="" className="w-full h-full object-contain p-1" />
                    : <Building2 className="w-5 h-5" style={{ color: colour }} />}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{o.name}</p>
                  <p className="text-xs text-slate-300">{(o.org_type || '').replace(/_/g, ' ')} · {o.status}</p>
                </div>
              </div>
              <p className="text-xs text-slate-300 mt-3 font-mono truncate">{o.portal_url}</p>

              <div className="mt-3" data-testid={`org-seats-${o.slug}`}>
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {s.label}</span>
                  {s.full && <span className="text-amber-300">full</span>}
                </div>
                {!!s.total && (
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: pct >= 100 ? '#f59e0b' : colour }} />
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-3 text-xs text-slate-300">
                <span>{o.programs} programs</span>
                <span>{o.staff_count} staff</span>
                {o.credits_per_seat != null && (
                  <span className="text-slate-300">{o.credits_per_seat}/seat internal</span>
                )}
              </div>
              <div className="mt-4 rounded-xl border border-white/10 bg-[#0a1729]/40 p-3"
                data-testid={`instance-${o.slug}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Their instance</p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Button asChild size="sm" className="rounded-full bg-slate-800 hover:bg-slate-700 text-white">
                    <Link to={`/app/org?org=${o.id}`} data-testid={`open-org-${o.slug}`}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Their console
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="ghost" className="rounded-full text-slate-300">
                    <a href={`/${o.slug}`} target="_blank" rel="noreferrer" data-testid={`portal-${o.slug}`}>
                      Their front door <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </Button>
                  {(o.invite_codes || []).slice(0, 1).map(code => (
                    <button key={code} onClick={() => copy(code)} data-testid={`copy-code-${o.slug}`}
                      className="rounded-full border border-white/15 px-3 py-1 text-xs font-mono text-slate-300 hover:border-white/40 transition-colors">
                      <Copy className="w-3 h-3 inline mr-1" />{code}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-300 mt-2.5">
                  Console = where they run programmes, roster and consent. Front door = the branded
                  page their families land on.
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white font-outfit text-xl">New organization</DialogTitle></DialogHeader>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">Organization name</Label>
              <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
                className="input-dark" data-testid="org-name" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">What is this?</Label>
              <div className="grid sm:grid-cols-3 gap-2">
                {LICENCES.map(l => (
                  <button key={l.key} type="button" onClick={() => setF({ ...f, license_type: l.key })}
                    data-testid={`licence-${l.key}`}
                    className={`text-left rounded-xl border p-3 transition-colors ${f.license_type === l.key
                      ? 'border-white/40 bg-white/[0.06]' : 'border-white/10 hover:border-white/25'}`}>
                    <p className="text-sm text-white font-medium">{l.label}</p>
                    <p className="text-xs text-slate-300 mt-1">{l.line}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Type of organization</Label>
              <Select value={f.org_type} onValueChange={v => setF({ ...f, org_type: v })}>
                <SelectTrigger className="input-dark" data-testid="org-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Portal address</Label>
              <Input value={f.slug} onChange={e => checkSlug(e.target.value)}
                placeholder="from the name" className="input-dark" data-testid="org-slug" />
              <p className="text-xs text-slate-300" data-testid="slug-hint">
                {slugState
                  ? `${slugState.portal_url} ${slugState.available ? '· available' : '· taken'}`
                  : 'agenticfuturepath.com/your-organization'}
              </p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">What they do (shown on their portal)</Label>
              <Textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
                className="input-dark min-h-[70px]" data-testid="org-description" />
            </div>

            {/* branding */}
            <div className="space-y-2 sm:col-span-2 rounded-xl border border-white/10 bg-slate-800/30 p-4">
              <Label className="text-slate-300 text-sm">Their logo, their colours come from it</Label>
              <p className="text-xs text-slate-300">
                Upload the logo and we read its colours and build their whole front door in them.
                Only set a colour by hand if you want to override what the logo gives us.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <div className="w-16 h-16 rounded-xl border border-white/15 flex items-center justify-center overflow-hidden"
                  style={{ background: `${f.primary_color}18` }}>
                  {f.logo_url
                    ? <img src={f.logo_url} alt="" className="w-full h-full object-contain p-1" data-testid="logo-preview" />
                    : <Building2 className="w-6 h-6 text-slate-300" />}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={e => pickLogo(e.target.files?.[0])} data-testid="org-logo-file" />
                <Button type="button" onClick={() => fileRef.current?.click()} data-testid="org-logo-btn"
                  className="rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload logo
                </Button>
                {['primary_color', 'secondary_color'].map(k => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-xs text-slate-300">{k.split('_')[0]}</span>
                    <input type="color" value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })}
                      className="w-9 h-9 rounded-lg bg-transparent border border-white/15 cursor-pointer"
                      data-testid={`org-${k}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* what they bought */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Places (students)</Label>
              <Input type="number" value={f.seat_count} onChange={e => setF({ ...f, seat_count: e.target.value })}
                placeholder="e.g. 150" className="input-dark" data-testid="org-seats" />
              <p className="text-xs text-slate-300">Changeable any time as they grow.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">License fee (annual, USD)</Label>
              <Input type="number" value={f.license_fee} onChange={e => setF({ ...f, license_fee: e.target.value })}
                placeholder="e.g. 12000" className="input-dark" data-testid="org-fee" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">AI allowance per place, internal only</Label>
              <Input type="number" value={f.credits_per_seat}
                onChange={e => setF({ ...f, credits_per_seat: e.target.value })}
                placeholder="250 (default)" className="input-dark" data-testid="org-credits-per-seat" />
              <p className="text-xs text-slate-300">
                What we set aside to run the AI behind each place. Never shown to the organization or
                its participants, only FuturePath staff see this.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Ages from</Label>
              <Input type="number" value={f.participant_age_min}
                onChange={e => setF({ ...f, participant_age_min: e.target.value })}
                className="input-dark" data-testid="org-age-min" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Ages to</Label>
              <Input type="number" value={f.participant_age_max}
                onChange={e => setF({ ...f, participant_age_max: e.target.value })}
                className="input-dark" data-testid="org-age-max" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Location</Label>
              <Input value={f.location} onChange={e => setF({ ...f, location: e.target.value })}
                className="input-dark" data-testid="org-location" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Website</Label>
              <Input value={f.website} onChange={e => setF({ ...f, website: e.target.value })}
                className="input-dark" data-testid="org-website" />
            </div>

            {/* their admin */}
            <div className="sm:col-span-2 rounded-xl border border-cyan-400/25 bg-cyan-500/5 p-4 space-y-3">
              <Label className="text-slate-200 text-sm">Their administrator</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={f.admin_name} onChange={e => setF({ ...f, admin_name: e.target.value })}
                  placeholder="Full name" className="input-dark" data-testid="org-admin-name" />
                <Input value={f.admin_email} onChange={e => setF({ ...f, admin_email: e.target.value })}
                  placeholder="Their email" className="input-dark" data-testid="org-admin-email" />
              </div>
              <p className="text-xs text-slate-300">
                We email them an invitation to create their own password. They land straight in their
                own console, their branding, their team, their places.
              </p>
            </div>
          </div>

          <Button onClick={create} disabled={saving || !f.name} data-testid="org-create-submit"
            className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold mt-2">
            {saving ? 'Creating…' : 'Create organization'}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700" data-testid="org-created-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-xl flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-400" /> {created?.name} is live
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-xl bg-slate-800/50 p-3">
              <p className="text-xs text-slate-300">Their portal</p>
              <p className="text-white font-mono text-xs mt-1 break-all">{created?.portal_url}</p>
            </div>
            <div className="rounded-xl bg-slate-800/50 p-3">
              <p className="text-xs text-slate-300">Places</p>
              <p className="text-white mt-1">{created?.seats?.label}</p>
            </div>
            {created?.admin ? (
              <div className="rounded-xl bg-slate-800/50 p-3">
                <p className="text-xs text-slate-300">Invitation sent to {created.admin.email}</p>
                <p className="text-white font-mono text-[11px] mt-1 break-all" data-testid="admin-invite-link">
                  {created.admin.invite_link}
                </p>
                <Button size="sm" onClick={() => copy(created.admin.invite_link)}
                  data-testid="copy-invite-link"
                  className="rounded-full bg-slate-700 hover:bg-slate-600 text-white mt-2">
                  <Copy className="w-3 h-3 mr-1" /> Copy link
                </Button>
              </div>
            ) : (
              <p className="text-slate-300">No administrator invited yet, you can invite one from the console.</p>
            )}
            <Button asChild className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold w-full">
              <Link to={`/app/org?org=${created?.organization?.id}`} data-testid="open-new-org">
                Open their console
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </Shell>
  );
}
